import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import Immutable from 'immutable';

import Icon from '@mapbox/mr-ui/icon';
import Copiable from '@mapbox/mr-ui/copiable';
import Heading from '@mapbox/mr-ui/heading';
import ChevronousText from '@mapbox/mr-ui/chevronous-text';
import LoaderLocal from '@mapbox/mr-ui/loader-local';

import { getUser } from '../util/mapbox-client';
import { store } from '../redux/store';

import {
  DownloadItem,
  PageWrapper,
  NewTabLink,
  PageShell
} from '../components';

import PageNotAuthorized from './403.js';
import LogoStarsBlack from '../svg/logo-stars-black.svg';

import {
  getAtlasServerSoftwareInfo,
  getAtlasDataInfo
} from '../redux/legacy/action-creators';

function mapStateToProps(state) {
  return {
    atlasV1NotAuthorized: state.get('atlasV1NotAuthorized'),
    licenseKey: state.get('atlasv1Info', Immutable.Map()).get('licenseKey'),
    mapDataInfo: state
      .get('atlasv1Data', Immutable.List())
      .filter(d => d.get('id').indexOf('places') === -1),
    geocodeDataInfo: state
      .get('atlasv1Data', Immutable.List())
      .filter(d => d.get('id').indexOf('places') > 1),
    latest: state.get('atlasv1Info', Immutable.Map()).getIn(['releases', 0]),
    isLoading: state.get('isLoading')
  };
}

function removeOldVersions(info) {
  let ids = Immutable.Set();
  return info
    .map(i => i.set('_date', dayjs(info.get('date'))))
    .sort((a, b) => a.get('_date') - b.get('_date'))
    .filter(i => {
      if (!ids.has(i.get('id'))) {
        ids = ids.add(i.get('id'));
        return true;
      } else {
        return false;
      }
    })
    .map(i => i.remove('_date'));
}

let LegacyAtlasServer = class LegacyAtlasServer extends React.Component {
  fetchData() {
    return Promise.all([
      getUser(),
      store.dispatch(getAtlasServerSoftwareInfo()),
      store.dispatch(
        getAtlasDataInfo([
          'mapbox.mapbox-streets-v5',
          'mapbox.mapbox-terrain-v1',
          'mapbox.satellite-full',
          'mapbox.satellite-watermask',
          'mapbox.places-aa-country-both',
          'mapbox.places-aa-region-both',
          'mapbox.places-zz-place-both',
          'mapbox.places-us-place-both',
          'mapbox.places-us-postcode-both',
          'mapbox.places-us-addressitp-both'
        ])
      )
    ]);
  }

  componentDidMount() {
    this.fetchData();
  }

  getSidebarSections() {
    return [
      {
        body: (
          <div>
            <a
              className="block mb12 link"
              href="https://www.mapbox.com/atlas-server"
              target="_blank"
            >
              <ChevronousText text="Atlas documentation" />
            </a>
            <a className="block link" href="https://www.mapbox.com/contact/">
              <ChevronousText text="Contact us" />
            </a>
          </div>
        )
      },
      {
        heading: 'Production requirements',
        body: (
          <div>
            <label className="txt-bold block">Operating system</label>
            <div className="mb12 color-gray">Ubuntu 14.04 or CentOS 7</div>
            <label className="txt-bold block">Hardware</label>
            <ul className="mb12">
              <li>16 CPU cores</li>
              <li>32 GB RAM</li>
              <li>512 GB SSD</li>
            </ul>
            <label className="txt-bold strong">Data</label>
            <ul>
              <li>Mapbox Streets</li>
              <li>Mapbox Terrain</li>
              <li>Mapbox Satellite</li>
            </ul>
          </div>
        )
      }
    ];
  }

  renderNoLatestInstaller = () => (
    <PageShell>
      <PageWrapper
        sectionName="Atlas Server"
        sidebarSections={this.getSidebarSections()}
      >
        <div className="py60 flex-parent flex-parent--column">
          <div className="flex-child flex-child--grow align-center">
            <div className="mx-auto pb18" style={{ maxWidth: 150 }}>
              <LogoStarsBlack />
            </div>
            <h1 className="txt-h1-mm txt-h2 txt-fancy">
              Well that wasn't supposed to happen...
            </h1>
            <p className="txt-l mb12 mt12">
              Something went wrong preparing the Atlas installers - either try
              again or{' '}
              <NewTabLink href="https://mapbox.com/contact/" className="link">
                contact us.
              </NewTabLink>
            </p>
          </div>
        </div>
      </PageWrapper>
    </PageShell>
  );

  render() {
    // Turn all untagged map data releases into a single flat array,
    // so we can print these first
    const mapDataInfoUntagged = removeOldVersions(
      this.props.mapDataInfo.reduce(
        (list, data) =>
          list.concat(
            data
              .get('download')
              .filter(d => !d.get('tag'))
              .map(d => d.set('id', data.get('id')))
          ),
        Immutable.List()
      )
    );

    const mapDataInfoTagged = removeOldVersions(
      this.props.mapDataInfo.reduce(
        (list, data) =>
          list.concat(
            data
              .get('download')
              .filter(d => d.get('tag'))
              .map(d => d.set('id', data.get('id')))
          ),
        Immutable.List()
      )
    );

    // XHR for the software meta data has returned,
    // and we were not authorized to access atlas-server (atlas v1):
    if (this.props.atlasV1NotAuthorized === true) {
      return <PageNotAuthorized pathname="/atlas-server" />;
    }
    if (this.props.isLoading)
      return (
        <PageShell>
          <PageWrapper
            sectionName="Atlas Server"
            sidebarSections={this.getSidebarSections()}
          >
            <div className="relative h-full w-full">
              <LoaderLocal />
            </div>
          </PageWrapper>
        </PageShell>
      );
    if (!this.props.latest) return this.renderNoLatestInstaller();

    return (
      <PageShell>
        <PageWrapper
          sectionName="Atlas Server"
          sidebarSections={this.getSidebarSections()}
        >
          <div>
            <div className="mb36 round txt-sm bg-yellow-light mb12 px12 py12">
              This version of Atlas is no longer in active development.{' '}
              <Link className="link txt-bold" to="/">
                Upgrade to the new Atlas.
              </Link>
            </div>
            <h4 className="mt24">
              <Heading variant="minor">Download Atlas Server</Heading>
            </h4>
            <span className="flex-child color-gray txt-s mb12">
              Latest release {this.props.latest.get('version')}, Updated{' '}
              {dayjs(this.props.latest.get('date')).format('YYYY-MM-DD')}
            </span>
            <div className="grid mb24">
              {this.props.latest.get('linux') && (
                <a
                  href={this.props.latest.get('linux')}
                  className="btn px24 round-full txt-s col col--6 transition"
                >
                  <Icon themeIcon="mr6" name="arrow-down" inline={true} />
                  Download for Linux
                </a>
              )}
              {this.props.latest.get('darwin') && (
                <a
                  href={this.props.latest.get('darwin')}
                  className="col col--6 align-center txt-s link"
                >
                  <Icon themeIcon="mr6" name="arrow-down" inline={true} />
                  OS X dev preview
                </a>
              )}
            </div>
            <div className="mb36 px24 py24 border border--gray-light round mt36">
              <label className="mb12 block txt-bold txt-s relative">
                License key
              </label>
              <Copiable
                value={this.props.licenseKey || 'Loading...'}
                truncated={true}
              />
              <em className="mt6 block txt-s color-gray">
                Your license key is required to setup Atlas Server.
              </em>
            </div>
            <h4 className="my24">
              <Heading variant="minor">Download Mapbox data</Heading>
            </h4>
            <p>
              It's the whole world, in a file. These downloads contain all of
              the data you need to deploy worldwide street, terrain, and
              satellite maps on your own infrastructure.
            </p>

            <div className="mb36 mt24">
              {mapDataInfoUntagged.map(download => (
                <DownloadItem
                  key={download.get('id')}
                  id={download.get('id')}
                  url={download.get('url')}
                  date={download.get('date')}
                  tag="Production"
                  size={download.get('size')}
                  dev={false}
                />
              ))}
            </div>

            <h4 className="my24">
              <Heading variant="minor">Download Geocoder data</Heading>
            </h4>
            <p>
              Alas Server does geocoding too! Download regional files for global
              coarse coverage and high-resolution, high-accuracy data for the
              United States.
            </p>

            <div className="mt24">
              {this.props.geocodeDataInfo.map(data =>
                data
                  .get('download')
                  .map(download => (
                    <DownloadItem
                      key={data.get('id')}
                      id={data.get('id')}
                      url={download.get('url')}
                      date={download.get('date')}
                      tag={download.get('tag')}
                      size={download.get('size')}
                      dev={false}
                    />
                  ))
              )}
            </div>

            <div className="px24 py24 border border--gray-light round mt36 pb0">
              <h4 className="my24">
                <Heading variant="minor">Testing data For development</Heading>
              </h4>
              <p>
                Development snapshots are available for local testing. They're
                smaller, in order to be easier to download and try out, but they
                don't contain the whole world, so you won't want to deploy with
                these.
              </p>
              <div className="mt24">
                {mapDataInfoTagged.map(download => (
                  <DownloadItem
                    key={download.get('id')}
                    id={download.get('id')}
                    url={download.get('url')}
                    date={download.get('date')}
                    tag="Development"
                    size={download.get('size')}
                    dev={true}
                  />
                ))}
              </div>
            </div>
          </div>
        </PageWrapper>
      </PageShell>
    );
  }
};

LegacyAtlasServer.propTypes = {
  atlasV1NotAuthorized: PropTypes.bool,
  softwareInfo: PropTypes.instanceOf(Immutable.Map),
  mapDataInfo: PropTypes.instanceOf(Immutable.List),
  geocodeDataInfo: PropTypes.instanceOf(Immutable.List),
  latest: PropTypes.instanceOf(Immutable.Map)
};

LegacyAtlasServer = connect(mapStateToProps)(LegacyAtlasServer);

export { LegacyAtlasServer };
